import { useEffect, useState } from "react"
import { useGetDucInfoByIDQuery, useGetDUCInfoQuery } from "../../api/ducApiSlice";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { useUserInfoQuery } from "../../api/userApiSlice";
import { colours } from "../../StyleConstants";
import useGetStepHookDucProcess from "./UseGetStepHookDucProcess";
import { useNavigate } from "react-router-dom";
import {
    RenderCurrentDucStep
} from "./components/DucFlows";

export enum DUCProcessSteps {
    step1 = 1,
    step2 = 2,
    step3 = 3,
    step4 = 4,
    step5 = 5,
    step6 = 6,
    step7 = 7
}

export enum steps {
    RENEWAL = 'Renewal',
    REVIEW = 'Review',
    AUTHORIZEDOFFICIAL = 'AuthorizedOfficial',
    SUBRECIPIENTS = 'SubRecipients',
    ATTESTATION = 'Attestation',
    STATEMENT = 'Statement',
    RECIPIENTS = 'Recipients',
}

const DUCProcess = () => {
    const { data: userData } = useUserInfoQuery({});
    const [curStep, setCurStep] = useState(DUCProcessSteps.step1);
    const [DUCInfo, setDUCInfo] = useState({});
    const [dataForHook, setDataForHook] = useState({})
    const [expiryCheck, setExpiryCheck] = useState(false);
    const [fromStatement, setFromStatement] = useState(false);
    //Both of these local storage items get set in the PI modal
    const formType = localStorage.getItem('formType') || ""
    const isGroup = localStorage.getItem('isGroup') === "true"
    //This gets set in the general and aian block component respectively
    const isEdit = localStorage.getItem('edit') === "true"
    const isAddSub = localStorage.getItem('AddSub') === "true"
    const oldStatus = localStorage.getItem('status')
    //This is and setID is needed to call the useGetDucInfoByIDQuery it
    //relies on the ducID which is received from the useGetDUCInfoQuery call
    const [id, setID] = useState(0)
    const [prevData, setPrevData] = useState({})
    const [isSub, setIsSub] = useState(false)
    const [isSecond, setIsSecond] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [stepLabels, setStepLabels] = useState([]);
    const [loadingPrevData, setloadingPrevData] = useState(true)
    const [skip, setSkip] = useState(true)
    const navigate = useNavigate();
    const { data: DUCData, refetch, isLoading } = useGetDUCInfoQuery({ email: userData['email'] })
    const { data: dataByID, } = useGetDucInfoByIDQuery({ id: id }, { skip: skip })
    // @ts-ignore
    const attestationEnabled = window.env.attestationEnabled;


    useEffect(() => {
        // Prevent browser from restoring the page from back/forward cache (bfcache)
        // When a user navigates away and returns using the browser's Back/Forward buttons,
        // some browsers (like Chrome and Safari) may restore the page from memory (bfcache)
        // instead of re-running useEffect or fetching fresh data.
        // This check reloads the page if it was loaded from bfcache to ensure state is clean.
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                // Force reload if page is restored from bfcache
                window.location.reload();
            }
        };

        window.addEventListener("pageshow", handlePageShow);
        return () => {
            window.removeEventListener("pageshow", handlePageShow);
        };
    }, []);


    //This refetches the useGetDucInfoByIDQuery because on first call it will fail since we do not
    //have an id yet and are still waiting for from useGetDucInfoByIDQuery once it gets set we can
    //refetch and will receive the correct information
    useEffect(() => {
        if(id !== 0 ){
            setSkip(false)
        }

    }, [id]);


    useEffect(() => {
        if (!isEdit && oldStatus === "NotSubmitted") {
            setPrevData({})
            setloadingPrevData(false)
        } else if (dataByID) {
            setPrevData(dataByID)
            setloadingPrevData(false)
        }

    }, [dataByID]);

    useEffect(() => {
        if(userData?.['email']){
            refetch()
        }
    }, [userData]);


    useEffect(() => {
        if(oldStatus === null){
            navigate("/duc")
        }
    }, [oldStatus]);


    useEffect(() => {
        if (DUCData) {
            setIsSub(DUCData?.subApplicantGeneral)
            if(DUCData?.[formType]?.[1]){
                if(DUCData?.[formType]?.[1]?.status !== oldStatus && !submitting){
                    navigate("/duc")
                }
                if (DUCData?.[formType]?.[1]?.status === "Declined"){
                    setDataForHook(DUCData?.[formType]?.[0])
                    setID(DUCData?.[formType]?.[0]?.ducDetailsId)
                }
                else {
                    setDataForHook(DUCData?.[formType]?.[1])
                    setID(DUCData?.[formType]?.[1]?.ducDetailsId)
                }
                setIsSecond(true)
            } else {
                if(DUCData?.[formType]?.[0]?.status !== oldStatus && !submitting){
                    navigate("/duc")
                }
                setDataForHook(DUCData?.[formType]?.[0])
                setID(DUCData?.[formType]?.[0]?.ducDetailsId)
            }
            return
        }
    }, [formType, DUCData]);

    //This hook just determines what step of the duc process we should currently be on
    //depending on status and renewal and if it's a group duc and also if it's being edited and resubmitted
    useGetStepHookDucProcess(dataForHook, setDUCInfo, setCurStep, setExpiryCheck, setStepLabels, expiryCheck, DUCInfo,
        formType, userData['username'], isGroup, isEdit, isAddSub, isSub)


    return (
        <Stack flexDirection="column" gap={4} height="100%" marginX="50px">
            <Stack gap={3}>
                <Typography
                    variant="h1"
                    fontWeight="600"
                    fontSize="34px"
                    textAlign="center"
                    color={colours.midnightBlue.blue}>
                    Data Use Certification for NIH Brain Development Cohorts
                </Typography>
                {formType === "AIAN" &&
                    <Typography
                        fontWeight="400"
                        textAlign="center"
                        fontSize="22px">
                        Data from American Indian/Alaska Native Individuals (A-DUC)
                    </Typography>}
            </Stack>
            {(isLoading || loadingPrevData) &&
                <Stack width="100%" height="100%" flexDirection="column" alignItems="center" justifyContent="center">
                    <CircularProgress size={100} sx={{ width: "250px", height: "250px" }}/>
                </Stack>}

            {!isLoading && !loadingPrevData &&
            <RenderCurrentDucStep
                curStep={curStep}
                setCurStep={setCurStep}
                stepLabels={stepLabels}
                prevData={prevData}
                DUCInfo={DUCInfo}
                setDUCInfo={setDUCInfo}
                attestation={attestationEnabled}
                isEdit={isEdit}
                isSub={isSub}
                type={formType}
                data={dataForHook}
                isGroup={isGroup}
                refetch={refetch}
                expiry={expiryCheck}
                fromStatement={fromStatement}
                isAddSub={isAddSub}
                isSecond={isSecond}
                setSubmitting={setSubmitting}
                setFromStatement={setFromStatement}
                step={curStep}

            />}
        </Stack>
    )
}

export default DUCProcess